"use client";

import React from "react";
import {
  ChevronRight,
  Pencil,
  Trash2,
  Copy,
  Mail,
  Phone,
  QrCode,
} from "lucide-react";

type PayIdType = "payid" | "email" | "phone";

interface ContactListItemProps {
  /** Display name (full name or business name) */
  name: string;
  /** Initials for avatar (2 characters) */
  initials: string;
  /** Click handler for the entire row */
  onClick?: () => void;
  /** Asset icons to display (for wallet addresses) */
  assetIcons?: Array<{
    src: string;
    alt: string;
  }>;
  /** PayID type to show appropriate badge (payid, email, phone) */
  payIdType?: PayIdType;
  /** Whether the item is in edit mode */
  isEditMode?: boolean;
  /** Edit button click handler */
  onEdit?: () => void;
  /** Delete button click handler */
  onDelete?: () => void;
  /** Copy button click handler */
  onCopy?: () => void;
  /** Custom right action slot */
  rightAction?: React.ReactNode;
  /** Whether to show the arrow button (default: true) */
  showArrow?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Whether this item is being deleted (shows loading state) */
  isDeleting?: boolean;
}

const ContactListItem: React.FC<ContactListItemProps> = ({
  name,
  initials,
  onClick,
  assetIcons,
  payIdType,
  isEditMode = false,
  onEdit,
  onDelete,
  onCopy,
  rightAction,
  showArrow = true,
  className = "",
  isDeleting = false,
}) => {
  // Render PayID type badge/icon - all use gradient background per Figma
  const renderPayIdBadge = () => {
    if (!payIdType) return null;

    const gradientClass =
      "w-6 h-6 rounded-full bg-gradient-to-br from-[#57249C] to-[#CE3070] flex items-center justify-center";

    switch (payIdType) {
      case "email":
        return (
          <div className={gradientClass}>
            <Mail className="w-3 h-3 text-white" />
          </div>
        );
      case "phone":
        return (
          <div className={gradientClass}>
            <Phone className="w-3 h-3 text-white" />
          </div>
        );
      case "payid":
      default:
        return (
          <div className={gradientClass}>
            <QrCode className="w-3 h-3 text-white" />
          </div>
        );
    }
  };
  // Render action buttons based on mode
  const renderActions = () => {
    // Custom right action takes precedence
    if (rightAction) {
      return rightAction;
    }

    if (isEditMode) {
      return (
        <>
          {onEdit && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
            >
              <Pencil className="w-4 h-4 text-[#6D6584]" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              disabled={isDeleting}
              className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
            >
              <Trash2 className="w-4 h-4 text-[#6D6584]" />
            </button>
          )}
        </>
      );
    }

    // View mode: copy and arrow
    return (
      <>
        {onCopy && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
          >
            <Copy className="w-4 h-4 text-[#6D6584]" />
          </button>
        )}
        {showArrow && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-[#6D6584]" />
          </button>
        )}
      </>
    );
  };

  return (
    <div
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-6 bg-white border border-[#EBE9EF] rounded-2xl hover:bg-[#F5F3F8] cursor-pointer transition-colors ${className}`}
    >
      <div className="flex items-center gap-3 flex-1">
        {/* Avatar with initials */}
        <div className="w-10 h-10 rounded-full bg-accent-red flex items-center justify-center text-white font-bold text-[12px] tracking-wider uppercase flex-shrink-0">
          {initials}
        </div>

        {/* Name */}
        <div className="flex-1 min-w-0">
          <p className="text-[20px] font-semibold text-text-primary truncate">
            {name}
          </p>
        </div>

        {/* Asset Icons (for wallet addresses) */}
        {assetIcons && assetIcons.length > 0 && (
          <div className="flex items-center -space-x-2 flex-shrink-0">
            {assetIcons.map((icon, index) => (
              <img
                key={index}
                src={icon.src}
                alt={icon.alt}
                className="w-6 h-6 rounded-full border border-white"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ))}
          </div>
        )}

        {/* PayID Type Badge */}
        {payIdType && (
          <div className="flex items-center flex-shrink-0">
            {renderPayIdBadge()}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {renderActions()}
        </div>
      </div>
    </div>
  );
};

export default ContactListItem;
